import { useEffect, useState } from "react";
import { api } from "../lib/api";
import AdminShell from "../components/AdminShell";
import { TrendingDown, ExternalLink, Filter, Loader2 } from "lucide-react";
import {
  ResponsiveContainer, FunnelChart, Funnel, LabelList, Tooltip,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from "recharts";

const RANGES = [
  { days: 7, label: "Last 7 Days" },
  { days: 30, label: "Last 30 Days" },
  { days: 0, label: "All Time" },
];
const FUNNEL_COLORS = ["#F59E0B", "#38BDF8", "#A78BFA", "#34D399", "#10B981"];

const tooltipStyle = {
  backgroundColor: "#0a0a0a", border: "1px solid #404040",
  borderRadius: "8px", color: "#e5e5e5", fontSize: "12px",
};

export default function AdminAnalytics() {
  const [days, setDays] = useState(30);
  const [audience, setAudience] = useState("founder");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/admin/conversion-analytics?days=${days}`)
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [days]);

  const isFunder = audience === "funder";
  const view = data ? {
    funnel: (isFunder ? data.funder_funnel : data.funnel) || [],
    quizSteps: (isFunder ? data.funder_quiz_steps : data.quiz_steps) || [],
    topAbandonment: (isFunder ? data.funder_top_abandonment : data.top_abandonment) || [],
  } : { funnel: [], quizSteps: [], topAbandonment: [] };

  const funnelData = view.funnel.map((s, i) => ({
    ...s, name: s.label, fill: FUNNEL_COLORS[i % FUNNEL_COLORS.length],
  }));

  return (
    <AdminShell>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold">Conversion Analytics</h1>
          <p className="mt-1 text-sm text-neutral-400">
            Landing → Quiz → Signup funnel, abandonment hotspots and cross-app activity.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex rounded-lg border border-neutral-800 bg-neutral-900 p-1" data-testid="analytics-audience-toggle">
            {[{ id: "founder", label: "Founders" }, { id: "funder", label: "Funders" }].map((a) => (
              <button key={a.id} data-testid={`analytics-audience-${a.id}`}
                onClick={() => setAudience(a.id)}
                className={"rounded-md px-3 py-1.5 text-xs font-bold transition-colors " +
                  (audience === a.id ? "bg-sky-500 text-neutral-950" : "text-neutral-400 hover:text-neutral-200")}>
                {a.label}
              </button>
            ))}
          </div>
          <div className="flex rounded-lg border border-neutral-800 bg-neutral-900 p-1" data-testid="analytics-range-toggle">
            {RANGES.map((r) => (
              <button key={r.days} data-testid={`analytics-range-${r.days}`}
                onClick={() => setDays(r.days)}
                className={"rounded-md px-3 py-1.5 text-xs font-bold transition-colors " +
                  (days === r.days ? "bg-[#F59E0B] text-neutral-950" : "text-neutral-400 hover:text-neutral-200")}>
                {r.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading && (
        <div className="mt-16 flex justify-center" data-testid="analytics-loading">
          <Loader2 className="h-6 w-6 animate-spin text-[#F59E0B]" />
        </div>
      )}

      {!loading && data && (
        <>
          {/* ── Funnel ── */}
          <div className="mt-8 rounded-2xl border border-neutral-800 bg-neutral-900 p-6" data-testid="analytics-funnel-card">
            <p className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-neutral-500">
              <Filter className="h-4 w-4 text-[#F59E0B]" /> Conversion funnel
            </p>
            <div className="mt-4 grid gap-6 lg:grid-cols-[1fr_320px]">
              <div className="h-72" data-testid="analytics-funnel-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <FunnelChart>
                    <Tooltip contentStyle={tooltipStyle} />
                    <Funnel dataKey="value" data={funnelData} isAnimationActive>
                      <LabelList position="right" dataKey="name" fill="#a3a3a3" stroke="none" fontSize={12} />
                    </Funnel>
                  </FunnelChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-2">
                {view.funnel.map((s) => (
                  <div key={s.key} data-testid={`funnel-step-${s.key}`}
                    className="flex items-center justify-between rounded-lg border border-neutral-800 bg-neutral-950 px-3 py-2">
                    <div>
                      <p className="text-xs font-semibold text-neutral-300">{s.label}</p>
                      {s.pct_of_prev !== null && (
                        <p className={"text-[10px] font-bold " + (s.pct_of_prev >= 50 ? "text-emerald-400" : "text-rose-400")}>
                          {s.pct_of_prev}% of previous step
                        </p>
                      )}
                    </div>
                    <p className="text-xl font-extrabold text-neutral-100">{s.value}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* ── Abandonment highlights ── */}
          <div className="mt-6 rounded-2xl border border-neutral-800 bg-neutral-900 p-6" data-testid="analytics-abandonment-card">
            <p className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-neutral-500">
              <TrendingDown className="h-4 w-4 text-rose-400" /> Abandonment highlights — where {isFunder ? "funders" : "founders"} stop the quiz
            </p>
            {view.topAbandonment.length === 0 ? (
              <p className="mt-4 text-sm text-neutral-500">No quiz drop-off recorded in this range.</p>
            ) : (
              <div className="mt-4 grid gap-3 md:grid-cols-3">
                {view.topAbandonment.map((d, i) => (
                  <div key={d.after_step} data-testid={`abandonment-top-${i + 1}`}
                    className="rounded-xl border border-rose-500/20 bg-rose-500/5 p-4">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-rose-300">
                      #{i + 1} drop-off · after Step {d.after_step}
                    </p>
                    <p className="mt-1 text-sm font-bold text-neutral-100">{d.question}</p>
                    <p className="mt-2 text-2xl font-extrabold text-rose-300">−{d.visitors_lost}</p>
                    <p className="text-[11px] text-neutral-500">
                      {d.drop_pct}% of the {d.visitors_reached} who reached it
                    </p>
                  </div>
                ))}
              </div>
            )}
            <div className="mt-6 h-56" data-testid="analytics-quiz-bar-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={view.quizSteps} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#262626" />
                  <XAxis dataKey="step" tickFormatter={(s) => `S${s}`} tick={{ fill: "#737373", fontSize: 11 }} />
                  <YAxis tick={{ fill: "#737373", fontSize: 11 }} allowDecimals={false} />
                  <Tooltip contentStyle={tooltipStyle}
                    labelFormatter={(s) => (view.quizSteps.find((q) => q.step === s) || {}).question || `Step ${s}`} />
                  <Bar dataKey="visitors" fill={isFunder ? "#38BDF8" : "#F59E0B"} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* ── Cross-app activity ── */}
          <div className="mt-6 rounded-2xl border border-neutral-800 bg-neutral-900 p-6" data-testid="analytics-crossapp-card">
            <p className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-neutral-500">
              <ExternalLink className="h-4 w-4 text-sky-400" /> Cross-app activity — outbound hops to sibling apps
            </p>
            <p className="mt-1 text-[11px] text-neutral-500">{data.notes?.cross_app}</p>
            {(data.cross_app || []).length === 0 ? (
              <p className="mt-4 text-sm text-neutral-500" data-testid="crossapp-empty">
                No outbound clicks recorded yet in this range — data accrues from now on.
              </p>
            ) : (
              <table className="mt-4 w-full text-sm">
                <thead>
                  <tr className="border-b border-neutral-800 text-left text-[10px] font-bold uppercase tracking-wider text-neutral-500">
                    <th className="pb-2">Destination app</th>
                    <th className="pb-2 text-right">Clicks</th>
                    <th className="pb-2 text-right">Unique users</th>
                  </tr>
                </thead>
                <tbody>
                  {data.cross_app.map((r) => (
                    <tr key={r.dest} data-testid={`crossapp-row-${r.dest}`} className="border-b border-neutral-800/60">
                      <td className="py-2.5 font-semibold text-neutral-200">{r.dest}</td>
                      <td className="py-2.5 text-right font-extrabold text-neutral-100">{r.clicks}</td>
                      <td className="py-2.5 text-right text-sky-300">{r.unique_users}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {days === 0 && (
            <p className="mt-4 text-[11px] text-neutral-600">
              Note: visitor counts are limited by the {data.notes?.page_views_ttl_days}-day page-view retention window; signup totals are true all-time.
            </p>
          )}
        </>
      )}
    </AdminShell>
  );
}
